import type { IconName, SelectOption } from '@camelot/components'
import type { COUNTRY_CODE, Location } from '~/types/location'

export const DEFAULT_CITY = 'berlin'

export interface ZoneArea {
  center: Location
  radius: number
}
export interface City {
  readonly id: string
  readonly locationId: number
  readonly name: string
  readonly country: COUNTRY_CODE
  readonly legalEntityId: number
  readonly zoneArea: ZoneArea
}

// TODO: Fetch this all dynamically and build a local json during build time based on:
//  https://urbandrive.atlassian.net/wiki/spaces/CUSTOMER/pages/443679288/Legal+entities+locations+documentation
// https://admin.free2move.com/web/pricing/lmm/#/locations?legalEntityId=11

export const BERLIN: City = {
  id: DEFAULT_CITY,
  locationId: 12,
  name: 'Berlin',
  country: 'de',
  legalEntityId: 10,
  zoneArea: {
    radius: 30_000,
    center: {
      latitude: 52.52,
      longitude: 13.405,
    },
  },
}

export const AMSTERDAM: City = {
  id: 'amsterdam',
  locationId: 5,
  name: 'Amsterdam',
  country: 'nl',
  legalEntityId: 1,
  zoneArea: {
    radius: 20_000,
    center: {
      latitude: 52.3676,
      longitude: 4.9041,
    },
  },
}

export const BUDAPEST: City = {
  id: 'budapest',
  locationId: 55,
  name: 'Budapest',
  country: 'hu',
  legalEntityId: 32,
  zoneArea: {
    radius: 20_000,
    center: {
      latitude: 47.4979,
      longitude: 19.0402,
    },
  },
}

export const COPENHAGEN: City = {
  id: 'copenhagen',
  locationId: 52,
  name: 'Copenhagen',
  country: 'dk',
  legalEntityId: 37,
  zoneArea: {
    radius: 25_000,
    center: {
      latitude: 55.6761,
      longitude: 12.5683,
    },
  },
}

export const FRANKFURT: City = {
  id: 'frankfurt',
  locationId: 33,
  name: 'Frankfurt',
  country: 'de',
  legalEntityId: 10,
  zoneArea: {
    radius: 30_000,
    center: {
      latitude: 50.1109,
      longitude: 8.6821,
    },
  },
}

export const HAMBURG: City = {
  id: 'hamburg',
  locationId: 3,
  name: 'Hamburg',
  legalEntityId: 10,
  country: 'de',
  zoneArea: {
    radius: 30_000,
    center: {
      latitude: 53.5511,
      longitude: 9.9937,
    },
  },
}

export const MADRID: City = {
  id: 'madrid',
  locationId: 36,
  name: 'Madrid',
  country: 'es',
  legalEntityId: 23,
  zoneArea: {
    radius: 20_000,
    center: {
      latitude: 40.435094,
      longitude: -3.694087,
    },
  },
}

export const MILAN: City = {
  id: 'milan',
  locationId: 20,
  name: 'Milan',
  country: 'it',
  legalEntityId: 20,
  zoneArea: {
    radius: 20_000,
    center: {
      latitude: 45.4642,
      longitude: 9.19,
    },
  },
}

export const MUNICH: City = {
  id: 'munich',
  locationId: 26,
  name: 'Munich',
  legalEntityId: 10,
  country: 'de',
  zoneArea: {
    radius: 30_000,
    center: {
      latitude: 48.1351,
      longitude: 11.582,
    },
  },
}

export const RHINELAND: City = {
  id: 'rhineland',
  locationId: 19,
  name: 'Rhineland',
  country: 'de',
  legalEntityId: 10,
  zoneArea: {
    radius: 50_000,
    center: {
      latitude: 51.0856,
      longitude: 6.8711,
    },
  },

}

export const ROME: City = {
  id: 'rome',
  locationId: 31,
  name: 'Rome',
  country: 'it',
  legalEntityId: 20,
  zoneArea: {
    radius: 20_000,
    center: {
      latitude: 41.9028,
      longitude: 12.4964,
    },
  },
}

export const STUTTGART: City = {
  id: 'stuttgart',
  locationId: 18,
  name: 'Stuttgart',
  country: 'de',
  legalEntityId: 10,
  zoneArea: {
    radius: 30_000,
    center: {
      latitude: 48.7758,
      longitude: 9.1829,
    },
  },
}

export const PARIS: City = {
  id: 'paris',
  locationId: 48,
  name: 'Paris',
  country: 'fr',
  legalEntityId: 3,
  zoneArea: {
    radius: 20_000,
    center: {
      latitude: 48.8566,
      longitude: 2.3522,
    },
  },
}

export const TURIN: City = {
  id: 'turin',
  locationId: 44,
  name: 'Turin',
  country: 'it',
  legalEntityId: 20,
  zoneArea: {
    radius: 20_000,
    center: {
      latitude: 45.0703,
      longitude: 7.6869,
    },
  },
}

export const ULM: City = {
  id: 'ulm',
  locationId: 1,
  name: 'Ulm',
  country: 'de',
  legalEntityId: 10,
  zoneArea: {
    radius: 30_000,
    center: {
      latitude: 48.3984,
      longitude: 9.9915,
    },
  },
}

export const VIENNA: City = {
  id: 'vienna',
  locationId: 7,
  name: 'Vienna',
  country: 'at',
  legalEntityId: 2,
  zoneArea: {
    radius: 20_000,
    center: {
      latitude: 48.2082,
      longitude: 16.3738,
    },
  },
}

export const CITIES: Array<City> = [
  AMSTERDAM,
  BERLIN,
  FRANKFURT,
  HAMBURG,
  MADRID,
  MILAN,
  MUNICH,
  PARIS,
  RHINELAND,
  ROME,
  STUTTGART,
  TURIN,
  VIENNA,
]

export const CITIES_RECORD: Record<string, City> = {
  amsterdam: AMSTERDAM,
  berlin: BERLIN,
  copenhagen: COPENHAGEN,
  frankfurt: FRANKFURT,
  hamburg: HAMBURG,
  madrid: MADRID,
  milan: MILAN,
  munich: MUNICH,
  paris: PARIS,
  rhineland: RHINELAND,
  rome: ROME,
  stuttgart: STUTTGART,
  turin: TURIN,
  ulm: ULM,
  vienna: VIENNA,
}

export const SORTED_CITIES_AS_SELECT_OPTIONS = CITIES
  .sort((cityA, cityB) => cityA.country !== cityB.country ? cityA.country.localeCompare(cityB.country) : cityA.id.localeCompare(cityB.id),
  )
  .map((city: City) => {
    const prefixIcon = `flag_${city.country.toLowerCase()}` as IconName
    const option: SelectOption = { label: city.name, value: city, prefixIcon }
    return option
  })

// These mappings will change during 2024 to Free2move. Ping Matthieu for exact dates.
const LE_GERMANY = 'Free2move Deutschland GmbH'
const LE_SPAIN = 'Free2move Iberia S.A.U.'
const LE_ITALY = 'Free2move Italia S.r.l.'
const LE_FRANCE = 'Free2move SAS'
const LE_AUSTRIA = 'Free2move Österreich GmbH'
const LE_NETHERLANDS = 'Free2move Nederland B.V.'

export const LEGAL_ENTITIES: Record<number, string> = {
  1: LE_GERMANY, // Ulm
  3: LE_GERMANY, // Hamburg
  5: LE_NETHERLANDS, // Amsterdam
  7: LE_AUSTRIA, // Vienna
  12: LE_GERMANY, // Berlin
  33: LE_GERMANY, // Frankfurt
  19: LE_GERMANY, // Rhineland
  18: LE_GERMANY, // Stuttgart
  26: LE_GERMANY, // Munich
  36: LE_SPAIN, // Madrid
  48: LE_FRANCE, // Paris
  20: LE_ITALY, // Milan
  31: LE_ITALY, // Rome
  44: LE_ITALY, // Turin
}
